/**
 * channel-logo-url
 * ----------------
 * URL + presentation helpers for rendering channel logos. Dependency-free and PURE — no
 * env and no next/config — so BOTH the server-side lineup parser and the
 * client-side lineup / compare / carousel components can import it safely.
 *
 * These helpers take `isOvLogo`, the decision the SERVER already made in
 * `pickChannelLogo`, which rides on the channel object to every render site. They
 * deliberately do NOT look the flag up themselves: `publicRuntimeConfig` is a
 * Pages-Router-only transport (Next 15's app-index.js carries no runtimeConfig), so a
 * client-side lookup would resolve differently under `?router-version=v2` than it did on
 * the server and desync the render. Passing the decision makes that impossible.
 *
 * WAT-11362: channels-service channels now carry an OV catalog logo (`imageURL`)
 * that is an ABSOLUTE url on a non-directv.com CDN (dtvcdn). The historical
 * render sites prepend `https://www.directv.com/` to the (directv-relative)
 * logo path, which would mangle an absolute OV url into
 * `https://www.directv.com/https://…` → a broken image. These helpers add
 * absolute-url tolerance while leaving the relative-path (logoUrl / flag-off)
 * behavior byte-identical to what ships in production today.
 */

/**
 * No-logo placeholder for the OV-logo error path in {@link handleChannelLogoError} — and ONLY
 * that path (it has exactly one reference).
 *
 * Flat 120x120 transparent PNG: it paints NO circle and NO shadow of its own, which is why
 * the chip treatment is left ON for it. The retired `Channel_Icon_No-Logo.svg` baked both in
 * and therefore had to be stripped back off.
 *
 * ⏱️ **Matches channels-service on the tiers running `release/26.08.26`, NOT yet on prod.**
 * Measured 2026-08-24 21:05Z on `/channel-lineup/`: test1 serves this PNG on 37 channels and
 * the legacy SVG on 1; prod serves the legacy SVG on 36 and this PNG on 0, because prod is
 * still on `release-26-08-14`. So until the channels-service prod deploy, a server-side
 * no-match and a browser-side OV 404 render DIFFERENT pictures in prod. That is expected and
 * temporary — do not "fix" it by reverting this constant.
 *
 * 🔴 **Converging the two is a PAIRED change with `OV_NO_LOGO_MARKER`**
 * (`channels-service-helpers.ts`). That marker substring-matches the channels-service
 * placeholder FILENAME so `pickChannelLogo` can fall back to `logoUrl`. Repoint either side
 * without updating it and the placeholder is classified as a real OV logo, so channels with a
 * good legacy `logoUrl` render blank — WAT-11362 AC#3 reversed, with no test failing anywhere.
 *
 * ⚠️ **The load-bearing token in this URL is the DAM content id `youlnv3nxj` and the `/web/`
 * rendition — not the filename.** A wrong filename under the right id still returns the image;
 * a wrong id or rendition 404s. The id is mutable (an unpublish/republish rotates it) and this
 * module is deliberately env-free, so there is no `process.env` override the way
 * `env-constant.ts` gives its other DAM assets. Verify the URL by hand after any DAM change.
 *
 * 🔴 NOT repo-wide, and not claimed to be. Six other sources still hold their own copy of the
 * legacy `Channel_Icon_No-Logo.svg` — `channel-lineup-parser.ts`,
 * `ChnlListingPageV2/server/parseData.ts`, three `FALLBACK_CHNL_ICON_URL` constants
 * (ChnlListingPageV2, custom-acq-compare-tool, dtv-agent-package-compare-tool) and hardcoded
 * `src=` attributes in ChnlComparisonGrid / NationalChnlTable. Those fire on a DIFFERENT
 * condition (the feed supplied no logo url at all, not an OV image that failed to load), so
 * they are out of scope here on purpose rather than by oversight — see WAT-11755.
 */
export const NO_LOGO_ASSET =
  "https://www.directv.com/assets/content/youlnv3nxj/web/channel-logo_missing-logo.png";

/** The blank placeholder the CHLUP icon banner used before WAT-11362. */
const EMPTY_SVG_ASSET =
  "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg";

/**
 * What a render site did on an image error BEFORE WAT-11362. Replayed verbatim when the
 * flag is off, so this change cannot alter production error behaviour ahead of the flag.
 * `"none"` = the site had no onError at all (browser broken-image icon).
 */
export type LegacyLogoErrorFallback = "none" | "hideParent" | "emptySvg";

/**
 * <img onError> guard for channel logos.
 *
 * OV can advertise an `imageURL` that 404s — verified live on News Mix and
 * Heroes & Icons, whose `.../chlogo-clb-guide/120/91` URLs return HTTP 404. That is
 * invisible at populate time (the field is a non-empty, well-formed URL), so the
 * placeholder check in pickChannelLogo can't catch it and the chip renders blank.
 * Swap to the no-logo asset once; the guard flag stops an error loop if that 404s too.
 *
 * `legacyFallback` is the pre-WAT-11362 behaviour of the CALLING site. With the flag off
 * we replay exactly that — the no-logo swap is part of the feature, not a free-standing
 * fix, so it must not ship before the flag does.
 *
 * `isOvLogo` follows the SAME rule as channelLogoSx / channelLogoChipFor: the server's
 * decision wins, and where it did not survive transit the url decides. Undefined here used
 * to mean "legacy", which left the two rules disagreeing on the surfaces that lose the field
 * — styled as OV by those helpers, error-handled as legacy by this one, so a 404 emptied the
 * chip instead of filling it. An explicit `false` from the server still wins.
 */
export function handleChannelLogoError(
  event: { currentTarget: HTMLImageElement },
  isOvLogo?: boolean,
  legacyFallback: LegacyLogoErrorFallback = "none"
): void {
  const img = event?.currentTarget;
  if (!img || img.dataset.logoFallbackApplied === "1") {
    return;
  }

  // The erroring <img> carries the url the style helpers keyed off, so no call site has to
  // pass it twice.
  const isOv = isOvLogo ?? isOvLogoUrl(img?.src);

  if (!isOv) {
    if (legacyFallback === "emptySvg") {
      img.dataset.logoFallbackApplied = "1";
      img.src = EMPTY_SVG_ASSET;
    } else if (legacyFallback === "hideParent" && img.parentElement) {
      img.parentElement.style.display = "none";
    }
    return;
  }

  // Swap the src and leave the styling alone. The element was styled from the ORIGINAL src,
  // so it carries the OV treatment (our drawn chip + inset) — which is exactly right for
  // NO_LOGO_ASSET now that it is flat artwork. Until WAT-11755 the asset was the legacy
  // `Channel_Icon_No-Logo.svg`, which painted its OWN circle and shadow and so sat as a
  // circle-in-a-circle inside ours; this function had to imperatively undo the chip (React
  // cannot re-derive styles from an imperative src swap). The flat asset removes the reason
  // for that undo, so the fallback now renders in the same container as every other tile.
  img.dataset.logoFallbackApplied = "1";
  img.src = NO_LOGO_ASSET;
}

/**
 * THE per-image style for a channel logo. Returns `{}` when the flag is off, so flag-OFF
 * renders byte-identically to production.
 *
 * Geometry (flag on): MUI CardMedia's img default is `width:100%` + `height:auto`, so a
 * 120x91 OV logo in a 104px chip becomes 104x78 — a short, block-level box pinned to the
 * top. `object-fit` centres artwork inside the ELEMENT, so a short element means a logo
 * that sits high whatever object-fit says. Forcing the element to fill the chip square
 * makes `contain` centre against the circle. `display:block` also kills the inline
 * baseline gap the old `paddingTop:"1.3px"` hack was compensating for. `aspect-ratio:1`
 * covers the chips that set width but no height, where `height:100%` collapses to auto.
 *
 * There is deliberately NO border-radius: a radius CLIPS replaced content — an A/B on NFL
 * Network proved it (same size/padding/object-fit; radius ON rendered "VETWORK" with the
 * N and K shaved, radius OFF rendered "NETWORK" intact). OV artwork is full-bleed, so any
 * clip eats glyphs. The circle lives on the CONTAINER instead (channelLogoChipFor).
 *
 * Inset applies to OV logos ONLY: OV wordmarks are full-bleed and need the wide inset to
 * sit inside the circle. Legacy `Channel_Icon_*.svg` is returned UNSTYLED (`{}`) — its
 * 261x261 canvas already paints its own white badge at 216 across (82.76%), which is the
 * exact diameter channelLogoChipSx scales the OV chip to, so the two read identically with
 * no styling at all.
 */
export function channelLogoSx(isOvLogo?: boolean, src?: string | null) {
  // The server's decision wins wherever it survives the data path; where it does not, the
  // url still tells us. See isOvLogoUrl — the compare tool and the CHLUP interactive tool
  // both lose the field in transit, and a missing chip on a dark surface is a real defect.
  const isOv = isOvLogo ?? isOvLogoUrl(src);
  // Legacy artwork already paints its own badge at the right size and position — touching it
  // is what made logos 21% larger than production and dragged the Local/RSN tag with them.
  if (!isOv) {
    return {};
  }
  return {
    display: "block",
    width: "100%",
    height: "100%",
    aspectRatio: "1",
    objectFit: "contain" as const,
    boxSizing: "border-box" as const,
    // Inset so the full-bleed OV wordmark sits inside the circle rather than running to its
    // edges. 16% keeps the artwork within the circle's inscribed square (1/√2 ≈ 70.7%).
    padding: "16%",
    // Cancel any radius the render site put on the IMG (several set 50% / 300px to fake the
    // circle). A radius on a replaced element clips its content — proven on NFL Network,
    // which rendered "VETWORK" with the outer glyphs shaved. The circle is the container's.
    borderRadius: 0
  };
}

/**
 * The legacy `Channel_Icon_*.svg` is a 261x261 canvas whose own white badge is only 216
 * across. 216 / 261 = 0.8276 — the fraction of its box a channel logo has always occupied
 * on directv.com. The drawn OV chip matches it so the two are indistinguishable in size.
 */
export const LEGACY_BADGE_RATIO = 0.8276;

/**
 * The white circular badge — drawn for OV logos ONLY, and on the CONTAINER, never the img
 * (a radius on a replaced element clips its content).
 *
 * Legacy assets already paint this exact treatment themselves: a white circle plus a
 * baked-in drop shadow (`feOffset dy=2`, `feGaussianBlur stdDeviation=7.5`,
 * `flood-opacity=.1`). Drawing ours behind them produced the double ring. The shadow here
 * mirrors those SVG filter values so an OV chip and a legacy badge read identically.
 */
export const channelLogoChipSx = {
  backgroundColor: "#FFFFFF",
  borderRadius: "50%",
  // Paint at exactly the 82.76% the legacy SVG paints its OWN badge at, so an OV chip and a
  // legacy badge are the same 66px circle in the same 80px box — pixel-identical to
  // production. `transform` scales the painting without touching layout, so nothing around
  // the logo (the Local/RSN tag, row heights, grid gaps) moves.
  transform: `scale(${LEGACY_BADGE_RATIO})`,
  boxShadow: "0px 2px 15px 0px rgba(0, 0, 0, 0.1)"
};

/**
 * Chip for OV logos only — legacy artwork supplies its own (see channelLogoChipSx).
 * Returns `{}` with the flag off so flag-OFF is byte-identical to production.
 */
/** Path segment identifying an OV catalog logo (`.../chlogo-clb-guide/120/91`). */
const OV_LOGO_MARKER = "chlogo-clb-guide";

/**
 * FALLBACK ONLY — infer "is this an OV logo?" from the url.
 *
 * The server's `isOvLogo` is authoritative and every surface should carry it. Some do not:
 * the compare tool runs its channels through ChannelLineUpProvider.getFromCmsFeed,
 * which rebuilds each channel field-by-field and drops fields it does not know about. There
 * is no safe way to re-attach it afterwards — two attempts to annotate the provider's output
 * blanked the entire tool — so where the decision is absent we infer it rather than silently
 * drop the chip.
 *
 * Only consulted when `isOvLogo` is undefined; an explicit `false` from the server always
 * wins. Confirmed lost on three paths so far: the compare tool (getFromCmsFeed rebuilds each
 * channel field-by-field), the CHLUP interactive tool on /affiliates, where OV wordmarks
 * rendered straight onto the dark surface with no white circle behind them, and the zip-sourced
 * local/RSN card builders (WAT-12562, `localLogoFields`), whose payload carries no flag.
 */
export function isOvLogoUrl(src?: string | null): boolean {
  return (src ?? "").includes(OV_LOGO_MARKER);
}

export function channelLogoChipFor(isOvLogo?: boolean, src?: string | null) {
  return (isOvLogo ?? isOvLogoUrl(src)) ? channelLogoChipSx : {};
}

/**
 * True when `url` is an absolute http(s) URL (carries its own scheme).
 * Returns `boolean` (NOT a `url is string` type guard on purpose): callers use
 * it on values already narrowed to `string`, where a type guard would narrow
 * the else-branch to `never` and break `.startsWith` (TS2339).
 */
export function isAbsoluteUrl(url?: string | null): boolean {
  return /^https?:\/\//i.test(url ?? "");
}

/**
 * Resolve a channel logo path to a renderable <img> src for the render sites
 * that UNCONDITIONALLY prepended the directv.com host. An absolute url (the OV
 * `imageURL`) is returned as-is; anything else keeps the exact legacy prepend
 * (`https://www.directv.com/${path}`), so every directv-relative logoUrl value
 * — i.e. every flag-off render — stays byte-identical to production.
 * `String(path)` keeps the return typed `string` (isAbsoluteUrl is not a guard).
 */
export function resolveChannelLogoSrc(path?: string | null): string {
  return isAbsoluteUrl(path) ? String(path) : `https://www.directv.com/${path}`;
}

/**
 * Absolute-tolerant prepend that also normalizes the leading slash and returns
 * "" for empty — the exact shape the server lineup parser and buildLocalRsnChannel
 * hand-rolled inline as `logo.includes("directv.com") ? logo : prepend`. Folds
 * those duplicated copies into one guarded helper: an absolute url (OV imageURL)
 * passes through untouched, a directv-relative path is prepended as before
 * (WAT-11362). Distinct from resolveChannelLogoSrc, which preserves the other
 * sites' literal `directv.com/${path}` form (leading double-slash and all).
 */
export function resolveRelativeLogoSrc(path?: string | null): string {
  if (!path) {
    return "";
  }
  return isAbsoluteUrl(path) ?
    path :
    `https://www.directv.com${path.startsWith("/") ? "" : "/"}${path}`;
}
